import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { SurgeryPrescriptionType } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { SurgeryPrescriptionState } from '../../entities';
import { SurgeryPrescriptionAction } from '../actions';

export const surgeryPrescriptionAdapter: EntityAdapter<SurgeryPrescriptionType> =
    createEntityAdapter<SurgeryPrescriptionType>();

export const initialState: SurgeryPrescriptionState = surgeryPrescriptionAdapter.getInitialState({
    error: null,
    isLoading: false,
});

export const surgeryPrescriptionReducer = createReducer(
    initialState,
    on(SurgeryPrescriptionAction.loadSurgeryPrescription, state => ({ ...state, isLoading: true })),
    on(SurgeryPrescriptionAction.loadSurgeryPrescriptionSuccess, (state, { data }) =>
        surgeryPrescriptionAdapter.setAll(data, {
            ...state,
            error: null,
            isLoading: false,
        }),
    ),
    on(SurgeryPrescriptionAction.loadSurgeryPrescriptionFailure, (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(SurgeryPrescriptionAction.loadSurgeryPrescriptionCancelled, state => ({
        ...state,
        isLoading: false,
        error: null,
    })),
    on(SurgeryPrescriptionAction.updateSurgeryPrescription, (state, action) =>
        surgeryPrescriptionAdapter.updateMany(action.update, state),
    ),
    on(SurgeryPrescriptionAction.upsertSurgeryPrescription, (state, action) =>
        surgeryPrescriptionAdapter.upsertMany(action.data, state),
    ),
    on(SurgeryPrescriptionAction.removeSurgeryPrescription, (state, { ids }) =>
        surgeryPrescriptionAdapter.removeMany(ids, state),
    ),
    /**
     * Defensive reset: belt-and-suspenders against any future regression of
     * `loadSurgeryPrescription$` (or any in-flight fetch this slice grows
     * later) that silently completes via `takeUntil(isEmptyEncounterId)`
     * without dispatching a terminal action. Same bug shape as the MD-9750
     * stuck-spinner family already fixed for daily-goals, procedure-
     * prescriptions and ventilation: a stuck `isLoading: true` would bury the
     * surgery form's `[uiLoadingOverlay]` over the Save button on first paint
     * after logout/login.
     */
    on(SurgeryPrescriptionAction.clearSurgeryPrescription, EncountersActions.clearEncounter, state =>
        surgeryPrescriptionAdapter.removeAll({
            ...state,
            isLoading: false,
            error: null,
        }),
    ),
    on(SurgeryPrescriptionAction.addSurgeryPrescriptionChanges, (state, { toRemoveIds, toUpdateEntities }) => {
        const newState = surgeryPrescriptionAdapter.removeMany(toRemoveIds, state);

        return surgeryPrescriptionAdapter.upsertMany(toUpdateEntities, newState);
    }),
);
